import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaCalendarAlt, FaSave } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import BottomNavbar from '../components/BottomNavbar';
import { fetchSemuaJadwal, simpanJadwal } from '../services/jadwalService';

const HARI = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];

function Jadwal() {
  const navigate = useNavigate();
  const siswaId = localStorage.getItem('user_id');
  const [jadwals, setJadwals] = useState([]);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const existingByDay = useMemo(
    () => Object.fromEntries(jadwals.map((item) => [item.hari, item])),
    [jadwals]
  );

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const data = await fetchSemuaJadwal(siswaId);
        if (!active) return;

        setJadwals(data);
        setForm(
          Object.fromEntries(
            HARI.map((hari) => {
              const item = data.find((jadwal) => jadwal.hari === hari);
              return [hari, {
                jam_masuk: item?.jam_masuk?.slice(0, 5) || '',
                jam_keluar: item?.jam_keluar?.slice(0, 5) || '',
              }];
            })
          )
        );
      } catch (loadError) {
        if (active) setError(loadError.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [siswaId]);

  const updateField = (hari, field, value) => {
    setForm((current) => ({
      ...current,
      [hari]: { ...current[hari], [field]: value },
    }));
  };

  const saveDay = async (hari) => {
    const values = form[hari] || {};
    setError('');
    setSuccess('');

    if (!values.jam_masuk || !values.jam_keluar) {
      setError(`Jam masuk dan jam keluar untuk hari ${hari} wajib diisi.`);
      return;
    }

    if (values.jam_keluar <= values.jam_masuk) {
      setError(`Jam keluar untuk hari ${hari} harus lebih besar dari jam masuk.`);
      return;
    }

    setSaving(hari);
    try {
      const existing = existingByDay[hari];
      const response = await simpanJadwal({
        hari,
        jam_masuk: values.jam_masuk,
        jam_keluar: values.jam_keluar,
      }, existing?.id);

      setJadwals((current) => {
        const next = current.filter((item) => item.hari !== hari);
        return [...next, response.data];
      });
      setSuccess(`Jadwal ${hari} berhasil disimpan.`);
    } catch (saveError) {
      const message = saveError.response?.data?.message;
      setError(typeof message === 'string' ? message : `Jadwal ${hari} gagal disimpan.`);
    } finally {
      setSaving(null);
    }
  };

  return (
    <Container className="py-4" style={{ minHeight: '100vh', paddingBottom: '90px' }}>
      <div className="d-flex align-items-center mb-3">
        <Button variant="link" className="p-0 me-3 text-dark" onClick={() => navigate('/dashboard')}>
          <FaArrowLeft />
        </Button>
        <div>
          <h4 className="fw-bold mb-1">Jadwal PKL</h4>
          <small className="text-muted">Isi jadwal presensi sesuai jadwal di tempat PKL.</small>
        </div>
      </div>

      <Alert variant="info" className="rounded-4">
        Jika jadwal belum tersedia, isi jam masuk dan jam keluar untuk hari yang diperlukan.
        Jadwal hanya bisa diisi untuk akun siswa sendiri.
      </Alert>

      {error && <Alert variant="danger" className="rounded-4">{error}</Alert>}
      {success && <Alert variant="success" className="rounded-4">{success}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <div className="d-grid gap-3">
          {HARI.map((hari) => {
            const existing = existingByDay[hari];
            const values = form[hari] || { jam_masuk: '', jam_keluar: '' };
            return (
              <Card key={hari} className="border-0 shadow-sm rounded-4">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle bg-primary-subtle text-primary p-3 me-3">
                      <FaCalendarAlt />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-0">{hari}</h6>
                      <small className="text-muted">{existing ? 'Jadwal tersimpan' : 'Belum diisi'}</small>
                    </div>
                  </div>
                  <div className="row g-2">
                    <Form.Group className="col-6">
                      <Form.Label className="small text-muted">Jam masuk</Form.Label>
                      <Form.Control
                        type="time"
                        value={values.jam_masuk}
                        onChange={(event) => updateField(hari, 'jam_masuk', event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="col-6">
                      <Form.Label className="small text-muted">Jam keluar</Form.Label>
                      <Form.Control
                        type="time"
                        value={values.jam_keluar}
                        onChange={(event) => updateField(hari, 'jam_keluar', event.target.value)}
                      />
                    </Form.Group>
                  </div>
                  <Button
                    className="w-100 mt-3"
                    variant="primary"
                    onClick={() => saveDay(hari)}
                    disabled={saving !== null}
                  >
                    {saving === hari ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" />Simpan {hari}</>}
                  </Button>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      <BottomNavbar />
    </Container>
  );
}

export default Jadwal;
